import { AnimatePresence, motion } from "framer-motion";
import { createPortal } from "react-dom";
import { useState, useEffect } from "react";
import type { ReactNode } from "react";

type Props = {
  open: boolean;
  onClose: () => void;
  description?: string;
  lang?: "ko" | "en" | "zh" | "ja";
};

const messages = {
  ko: { title: "구현 예정", defaultDesc: "본 개발에서 제공될 기능입니다.", close: "닫기" },
  en: { title: "Coming Soon", defaultDesc: "This feature will be available in the full release.", close: "Close" },
  zh: { title: "即将推出", defaultDesc: "此功能将在正式版本中提供。", close: "关闭" },
  ja: { title: "近日公開", defaultDesc: "この機能は本開発で提供される予定です。", close: "閉じる" },
};

export function ComingSoonModal({ open, onClose, description, lang = "ko" }: Props) {
  const msg = messages[lang];

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return createPortal(
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            key="modal"
            initial={{ opacity: 0, scale: 0.95, x: "-50%", y: "-50%" }}
            animate={{ opacity: 1, scale: 1, x: "-50%", y: "-50%" }}
            exit={{ opacity: 0, scale: 0.95, x: "-50%", y: "-50%" }}
            transition={{ type: "spring", stiffness: 400, damping: 30 }}
            className="fixed left-1/2 top-1/2 z-50 w-[360px] max-w-[90vw] rounded-2xl bg-white p-6 shadow-2xl"
          >
            <div className="mb-1 text-xs font-semibold uppercase tracking-widest text-brand-500">
              {msg.title}
            </div>
            <p className="mb-5 text-sm text-gray-600">{description ?? msg.defaultDesc}</p>
            <button
              onClick={onClose}
              className="w-full rounded-xl bg-brand-500 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-600 active:bg-brand-700"
            >
              {msg.close}
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
}

export function withComingSoon<P extends object>(
  Component: React.ComponentType<P>,
  modalLang?: "ko" | "en" | "zh" | "ja"
): React.ComponentType<P & { comingSoon?: boolean; comingSoonDesc?: string }> {
  return function WrappedWithComingSoon({
    comingSoon,
    comingSoonDesc,
    ...props
  }: P & { comingSoon?: boolean; comingSoonDesc?: string }) {
    const [open, setOpen] = useState(false);
    if (comingSoon) {
      return (
        <>
          <div onClick={() => setOpen(true)}>
            <Component {...(props as P)} />
          </div>
          <ComingSoonModal open={open} onClose={() => setOpen(false)} description={comingSoonDesc} lang={modalLang} />
        </>
      );
    }
    return <Component {...(props as P)} />;
  };
}

void (null as unknown as ReactNode);
